import React, { useState, useEffect, useCallback } from "react";
import { useTheme, alpha } from "@mui/material/styles";
import { useActiveTenant } from "../hooks/useActiveTenant";
import NoTenantSelected from "../components/NoTenantSelected";
import {
  fetchPendingCourseSubmissions,
  approveCourseSubmission,
  rejectCourseSubmission,
  updateSubmissionInstructors,
  fetchPendingSessionRequests,
  approveSessionRequest,
  rejectSessionRequest,
  fetchPendingRegistrationApprovals,
  approveRegistration,
  rejectRegistration,
  fetchInstructorSignups,
} from "../api/backend";
import InstructorSignupsTab from "./approvals/instructorSignups/InstructorSignupsTab";
import { countGroups } from "./approvals/instructorSignups/signupsModel";
import { fetchTenantUsersByRole } from "../api/apiClient";
import {
  Box,
  Typography,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Autocomplete,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  
  
  InputAdornment,
} from "@mui/material";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import SchoolIcon from "@mui/icons-material/School";
import GroupIcon from "@mui/icons-material/Group";
import ArticleIcon from "@mui/icons-material/Article";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";

const DS = {
  ink: "var(--color-ink)",
  ink2: "var(--color-ink-2)",
  ink3: "var(--color-ink-3)",
  ink4: "var(--color-ink-4)",
  line: "var(--color-hairline-cloud)",
  line2: "var(--color-surface-press-light)",
  surface2: "var(--color-surface-2)",
  surface3: "var(--color-surface-press-light)",
  primary50: "var(--color-primary-10)",
  danger: "#B42318",
  danger50: "rgba(180,35,24,.08)",
  danger100: "rgba(180,35,24,.18)",
  blue: "var(--color-primary)",
  shadowSm: "0 1px 0 rgba(20,24,40,.04), 0 1px 3px rgba(20,24,40,.06)",
};

function useDS() {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const secondary = theme.palette.secondary.main;
  const accent = theme.palette.warning.main;
  return {
    ...DS,
    primary,
    primary10: alpha(primary, 0.06),
    green: secondary,
    greenDk: theme.palette.secondary.dark,
    green50: alpha(secondary, 0.10),
    green100: alpha(secondary, 0.20),
    amber: accent,
    amber50: alpha(accent, 0.10),
    amber100: alpha(accent, 0.22),
  };
}

function getCatConfig(cat = "", ds) {
  const c = (cat || "").toLowerCase();
  if (c.includes("fire") || c.includes("ff"))
    return { bg: "rgba(194,65,12,.10)", color: "#C2410C" };
  if (c.includes("ems") || c.includes("medic") || c.includes("paramedic"))
    return { bg: DS.blue + "1a", color: DS.blue };
  if (c.includes("hazmat") || c.includes("haz"))
    return { bg: ds.amber50, color: ds.amber };
  if (c.includes("rescue") || c.includes("lifeguard") || c.includes("search"))
    return { bg: ds.green50, color: ds.green };
  if (c.includes("officer") || c.includes("police") || c.includes("law"))
    return { bg: ds.primary10, color: ds.primary };
  return { bg: DS.surface3, color: DS.ink3 };
}

function getInstrColor(name = "", ds) {
  const palette = [ds.primary, ds.green, DS.blue, DS.ink3, ds.amber, "#C2410C"];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return palette[Math.abs(h) % palette.length];
}

function InstrAvatar({ name = "" }) {
  const DS = useDS();
  const initials =
    name
      .split(" ")
      .filter(Boolean)
      .map((w) => w[0])
      .join("")
      .slice(0, 2)
      .toUpperCase() || "?";
  return (
    <Box
      sx={{
        width: 26, height: 26, borderRadius: "50%",
        background: getInstrColor(name, DS),
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 10, fontWeight: 800, color: "#fff", flexShrink: 0,
      }}
    >
      {initials}
    </Box>
  );
}

function CategoryPill({ cat }) {
  const DS = useDS();
  const { bg, color } = getCatConfig(cat, DS);
  return (
    <Box
      sx={{
        display: "inline-flex", alignItems: "center", gap: "5px",
        px: "9px", py: "3px", borderRadius: "999px", background: bg,
        fontSize: 11, fontWeight: 700, letterSpacing: ".04em",
        textTransform: "uppercase", color,
      }}
    >
      <Box sx={{ width: 5, height: 5, borderRadius: "50%", background: "currentColor" }} />
      {cat || "—"}
    </Box>
  );
}

function StatusPill({ status }) {
  const DS = useDS();
  const s = (status || "").toLowerCase();
  const cfgMap = {
    pending: { bg: DS.amber50, color: DS.amber, label: "Pending Review" },
    approved: { bg: DS.green50, color: DS.green, label: "Approved" },
    rejected: { bg: DS.danger50, color: DS.danger, label: "Rejected" },
  };
  const cfg = cfgMap[s] || { bg: DS.surface3, color: DS.ink3, label: status || "—" };
  return (
    <Box
      sx={{
        display: "inline-flex", alignItems: "center", gap: "5px",
        px: "10px", py: "4px", borderRadius: "999px",
        background: cfg.bg,
        fontSize: 11, fontWeight: 700, letterSpacing: ".04em",
        textTransform: "uppercase", color: cfg.color, whiteSpace: "nowrap",
      }}
    >
      <Box sx={{ width: 5, height: 5, borderRadius: "50%", background: "currentColor" }} />
      {cfg.label}
    </Box>
  );
}

function MainTabBtn({ active, onClick, label, count }) {
  const DS = useDS();
  return (
    <Box
      component="button"
      onClick={onClick}
      sx={{
        height: 36, px: "18px", borderRadius: "10px", border: 0,
        background: active ? DS.primary : "transparent",
        color: active ? "#fff" : DS.ink3,
        font: "inherit", fontSize: "13.5px", fontWeight: 700,
        cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "8px",
        transition: "all .14s",
        "&:hover": { color: active ? "#fff" : DS.ink },
      }}
    >
      {label}
      {count > 0 && (
        <Box
          component="span"
          sx={{
            fontSize: 11, fontWeight: 700, px: "6px", py: "1px",
            borderRadius: "999px",
            background: active ? "rgba(255,255,255,.22)" : DS.amber50,
            color: active ? "inherit" : DS.amber,
          }}
        >
          {count}
        </Box>
      )}
    </Box>
  );
}

function SubTabBtn({ active, onClick, label, count }) {
  const DS = useDS();
  const displayLabel = label === "all" ? "All" : label.charAt(0).toUpperCase() + label.slice(1);
  return (
    <Box
      component="button"
      onClick={onClick}
      sx={{
        height: 30, px: "13px", borderRadius: "8px", border: 0,
        background: active ? DS.primary10 : "transparent",
        color: active ? DS.primary : DS.ink3,
        font: "inherit", fontSize: 13, fontWeight: 600,
        cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "6px",
        transition: "all .13s",
        "&:hover": {
          color: active ? DS.primary : DS.ink,
          background: active ? DS.primary10 : DS.surface3,
        },
      }}
    >
      {displayLabel}
      <Box
        component="span"
        sx={{
          fontSize: "10.5px", fontWeight: 700, px: "5px", py: "1px", borderRadius: "5px",
          background: active ? DS.primary : DS.surface3,
          color: active ? "#fff" : DS.ink4,
        }}
      >
        {count}
      </Box>
    </Box>
  );
}

function DetailField({ label, children }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "5px" }}>
      <Typography
        sx={{
          fontSize: 11, fontWeight: 700, letterSpacing: ".07em",
          textTransform: "uppercase", color: DS.ink3,
        }}
      >
        {label}
      </Typography>
      {children}
    </Box>
  );
}

// Emails can open a tab directly, e.g. /coordinator/approvals?tab=signups from the instructor sign-up email.
const TAB_BY_PARAM = { submissions: 0, sessions: 1, registrations: 2, signups: 3 };

function tabFromUrl() {
  const tab = new URLSearchParams(window.location.search).get("tab");
  return Object.prototype.hasOwnProperty.call(TAB_BY_PARAM, tab) ? TAB_BY_PARAM[tab] : 0;
}

export default function CourseApprovalDashboard() {
  const DS = useDS();

  const TH_SX = {
    fontSize: 11, fontWeight: 700, letterSpacing: ".07em", textTransform: "uppercase",
    color: DS.ink3, background: "var(--color-surface-2)", whiteSpace: "nowrap",
    py: "11px", px: "16px", borderBottom: `1px solid ${DS.line2}`,
  };
  const TD_SX = {
    py: "14px", px: "16px",
    borderBottom: `1px solid ${DS.line2}`,
    verticalAlign: "middle",
  };
  const SEARCH_SX = {
    "& .MuiOutlinedInput-root": {
      height: 32, fontSize: 13, borderRadius: "9px", width: 200,
      "& fieldset": { borderColor: DS.line },
      "&:hover fieldset": { borderColor: DS.ink3 },
      "&.Mui-focused fieldset": { borderColor: DS.primary },
    },
  };
  const { tenantId: selectedTenantId } = useActiveTenant();

  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [drawerType, setDrawerType] = useState("course");
  const [detailOpen, setDetailOpen] = useState(false);
  const [drawerRejectMode, setDrawerRejectMode] = useState(false);
  const [drawerRejectText, setDrawerRejectText] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [mainTab, setMainTab] = useState(tabFromUrl);
  const [courseSubFilter, setCourseSubFilter] = useState("all");
  const [courseSearch, setCourseSearch] = useState("");
  const [sessionSubFilter, setSessionSubFilter] = useState("all");
  const [sessionSearch, setSessionSearch] = useState("");
  const [sessionRequests, setSessionRequests] = useState([]);
  const [sessionRequestsLoading, setSessionRequestsLoading] = useState(false);
  const [pendingRegistrations, setPendingRegistrations] = useState([]);
  const [pendingRegistrationsLoading, setPendingRegistrationsLoading] = useState(false);
  const [registrationActionId, setRegistrationActionId] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [signupGroups, setSignupGroups] = useState([]);
  const [signupsLoading, setSignupsLoading] = useState(true);
  const [signupsError, setSignupsError] = useState(null);

  const [instructorDialogOpen, setInstructorDialogOpen] = useState(false);
  const [instructorSubmission, setInstructorSubmission] = useState(null);
  const [allInstructors, setAllInstructors] = useState([]);
  const [selectedInstructors, setSelectedInstructors] = useState([]);
  const [instructorSaving, setInstructorSaving] = useState(false);

  const loadSubmissions = useCallback(() => {
    if (!selectedTenantId) {
      setSubmissions([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPendingCourseSubmissions(null, selectedTenantId)
      .then((data) => { setSubmissions(data); setLoading(false); })
      .catch(() => { setError("Failed to load course submissions."); setLoading(false); });
  }, [selectedTenantId]);

  useEffect(() => { loadSubmissions(); }, [loadSubmissions]);

  const loadSessionRequests = useCallback(() => {
    setSessionRequestsLoading(true);
    fetchPendingSessionRequests()
      .then((data) => { setSessionRequests(Array.isArray(data) ? data : []); setSessionRequestsLoading(false); })
      .catch(() => { setSessionRequests([]); setSessionRequestsLoading(false); });
  }, []);

  useEffect(() => {
    if (mainTab === 1) loadSessionRequests();
  }, [mainTab, loadSessionRequests]);

  const loadSignups = useCallback(() => {
    if (!selectedTenantId) { setSignupGroups([]); setSignupsLoading(false); return; }
    fetchInstructorSignups(selectedTenantId)
      .then((data) => { setSignupGroups(Array.isArray(data) ? data : []); setSignupsError(null); })
      .catch((e) => setSignupsError(e?.response?.data?.message || "Failed to load instructor sign-ups."))
      .finally(() => setSignupsLoading(false));
  }, [selectedTenantId]);

  // Loaded up front so the tab's badge and the header count are right before the tab is opened.
  useEffect(() => { loadSignups(); }, [loadSignups]);
  useEffect(() => {
    if (mainTab === 3) loadSignups();
  }, [mainTab, loadSignups]);

  const loadPendingRegistrations = useCallback(() => {
    setPendingRegistrationsLoading(true);
    fetchPendingRegistrationApprovals()
      .then((data) => { setPendingRegistrations(Array.isArray(data) ? data : []); setPendingRegistrationsLoading(false); })
      .catch(() => { setPendingRegistrations([]); setPendingRegistrationsLoading(false); });
  }, []);

  useEffect(() => {
    if (mainTab === 2) loadPendingRegistrations();
  }, [mainTab, loadPendingRegistrations]);

  const handleApproveRegistration = async (reg) => {
    setRegistrationActionId(reg.id);
    try {
      await approveRegistration(reg.classId, reg.id);
      loadPendingRegistrations();
      setSuccessMessage("Registration approved.");
    } catch {
      setError("Failed to approve registration.");
    } finally {
      setRegistrationActionId(null);
      setTimeout(() => setSuccessMessage(""), 3000);
    }
  };

  const handleRejectRegistration = async (reg) => {
    setRegistrationActionId(reg.id);
    try {
      await rejectRegistration(reg.classId, reg.id);
      loadPendingRegistrations();
      setSuccessMessage("Registration rejected.");
    } catch {
      setError("Failed to reject registration.");
    } finally {
      setRegistrationActionId(null);
      setTimeout(() => setSuccessMessage(""), 3000);
    }
  };

  const pendingCount = submissions.filter(
    (s) => (s.status || "").toUpperCase() === "PENDING"
  ).length;
  const approvedCount = submissions.filter(
    (s) => (s.status || "").toUpperCase() === "APPROVED"
  ).length;
  const rejectedCount = submissions.filter(
    (s) => (s.status || "").toUpperCase() === "REJECTED"
  ).length;
  const openSignupSlots = countGroups(signupGroups, Date.now()).openSlots;
  const pendingSessionCount = sessionRequests.filter(
    (r) => (r.status || "pending").toLowerCase() === "pending"
  ).length;

  const filteredSubmissions = submissions
    .filter(
      (s) => courseSubFilter === "all" || (s.status || "").toLowerCase() === courseSubFilter
    )
    .filter(
      (s) =>
        !courseSearch ||
        (s.title || "").toLowerCase().includes(courseSearch.toLowerCase()) ||
        (s.instructorName || "").toLowerCase().includes(courseSearch.toLowerCase())
    );

  const filteredSessions = sessionRequests
    .filter(
      (r) =>
        sessionSubFilter === "all" ||
        (r.status || "pending").toLowerCase() === sessionSubFilter
    )
    .filter(
      (r) =>
        !sessionSearch ||
        (r.className || "").toLowerCase().includes(sessionSearch.toLowerCase()) ||
        (r.instructorName || "").toLowerCase().includes(sessionSearch.toLowerCase())
    );

  const handleViewDetails = (item, type = "course") => {
    setSelectedSubmission(item);
    setDrawerType(type);
    setDetailOpen(true);
    setDrawerRejectMode(false);
    setDrawerRejectText("");
  };

  const handleCloseDetail = () => {
    setDetailOpen(false);
    setSelectedSubmission(null);
    setDrawerRejectMode(false);
    setDrawerRejectText("");
  };

  const handleApprove = async () => {
    if (!selectedSubmission) return;
    setActionLoading(true);
    try {
      if (drawerType === "course") await approveCourseSubmission(selectedSubmission.id);
      else await approveSessionRequest(selectedSubmission.id);
      handleCloseDetail();
      if (drawerType === "course") loadSubmissions();
      else loadSessionRequests();
      setSuccessMessage(
        drawerType === "course" ? "Course submission approved." : "Session approved and created."
      );
    } catch {
      setError(
        drawerType === "course" ? "Failed to approve submission." : "Failed to approve session request."
      );
    } finally {
      setActionLoading(false);
      setTimeout(() => setSuccessMessage(""), 3000);
    }
  };

  const handleRejectConfirm = async () => {
    if (!selectedSubmission) return;
    setActionLoading(true);
    try {
      if (drawerType === "course")
        await rejectCourseSubmission(selectedSubmission.id, drawerRejectText);
      else await rejectSessionRequest(selectedSubmission.id, drawerRejectText);
      handleCloseDetail();
      if (drawerType === "course") loadSubmissions();
      else loadSessionRequests();
      setSuccessMessage("Submission rejected.");
    } catch {
      setError("Failed to reject submission.");
    } finally {
      setActionLoading(false);
      setTimeout(() => setSuccessMessage(""), 3000);
    }
  };

  const handleManageInstructors = async (submission) => {
    setInstructorSubmission(submission);
    setSelectedInstructors(submission.qualifiedInstructorIds || []);
    setInstructorDialogOpen(true);
    try {
      const instructors = await fetchTenantUsersByRole(selectedTenantId, "INSTRUCTOR");
      setAllInstructors(Array.isArray(instructors) ? instructors : []);
    } catch {
      setError("Failed to load instructors.");
    }
  };

  const handleSaveInstructors = async () => {
    if (!instructorSubmission) return;
    setInstructorSaving(true);
    try {
      await updateSubmissionInstructors(instructorSubmission.id, selectedInstructors);
      setInstructorDialogOpen(false);
      setInstructorSubmission(null);
      loadSubmissions();
    } catch {
      setError("Failed to update instructors.");
    } finally {
      setInstructorSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!selectedTenantId) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <NoTenantSelected icon={AssignmentIcon} />
      </Container>
    );
  }

  const dCat = selectedSubmission
    ? drawerType === "course"
      ? selectedSubmission.category || ""
      : ""
    : "";
  const { bg: dCatBg, color: dCatColor } = getCatConfig(dCat, DS);
  const dIsPending = selectedSubmission
    ? (selectedSubmission.status || "pending").toLowerCase() === "pending"
    : false;

  return (
    <Box sx={{ py: 3 }}>
      <Container maxWidth="lg">
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}
        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage("")}>
            {successMessage}
          </Alert>
        )}

        <Box
          sx={{
            display: "flex", alignItems: "flex-start",
            justifyContent: "space-between", gap: 2, mb: "20px", flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: { xs: 24, sm: 30 }, fontWeight: 800,
                letterSpacing: "-.035em", color: DS.ink, lineHeight: 1,
                fontFamily: "var(--font-display)",
              }}
            >
              Course Approvals
            </Typography>
            <Typography sx={{ fontSize: 14, color: DS.ink3, mt: "5px" }}>
              Review and approve course proposals, session requests, and sign-ups from instructors
            </Typography>
          </Box>
        </Box>

        {/* Main tabs */}
        <Box
          sx={{
            display: "flex", gap: "4px", mb: "16px",
            background: "#fff", border: `1.5px solid ${DS.line}`,
            borderRadius: "14px", p: "4px", width: "fit-content",
          }}
        >
          <MainTabBtn
            active={mainTab === 0}
            onClick={() => setMainTab(0)}
            label="Course Submissions"
            count={pendingCount}
          />
          <MainTabBtn
            active={mainTab === 1}
            onClick={() => setMainTab(1)}
            label="Session Requests"
            count={pendingSessionCount}
          />
          <MainTabBtn
            active={mainTab === 2}
            onClick={() => setMainTab(2)}
            label="Pending Registrations"
            count={pendingRegistrations.length}
          />
          <MainTabBtn
            active={mainTab === 3}
            onClick={() => setMainTab(3)}
            label="Instructor Sign-ups"
            count={openSignupSlots}
          />
        </Box>

        {mainTab === 3 && (
          <InstructorSignupsTab groups={signupGroups} loading={signupsLoading} error={signupsError} onReload={loadSignups} tenantId={selectedTenantId} />
        )}

        {/* Course Submissions Panel */}
        {mainTab === 0 && (
          <Paper
            sx={{
              borderRadius: "18px", border: `1px solid ${DS.line}`,
              boxShadow: DS.shadowSm, overflow: "hidden",
            }}
          >
            <Box
              sx={{
                display: "flex", alignItems: "center", gap: 1,
                p: "12px 18px", borderBottom: `1px solid ${DS.line2}`,
                background: "#fff", flexWrap: "wrap",
              }}
            >
              <SubTabBtn active={courseSubFilter === "all"} onClick={() => setCourseSubFilter("all")} label="all" count={submissions.length} />
              <Box sx={{ width: "1px", height: 18, background: DS.line2, flexShrink: 0 }} />
              <SubTabBtn active={courseSubFilter === "pending"} onClick={() => setCourseSubFilter("pending")} label="pending" count={pendingCount} />
              <SubTabBtn active={courseSubFilter === "approved"} onClick={() => setCourseSubFilter("approved")} label="approved" count={approvedCount} />
              <SubTabBtn active={courseSubFilter === "rejected"} onClick={() => setCourseSubFilter("rejected")} label="rejected" count={rejectedCount} />
              <Box sx={{ ml: "auto" }}>
                <TextField
                  size="small"
                  placeholder="Search…"
                  value={courseSearch}
                  onChange={(e) => setCourseSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 14, color: DS.ink4 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={SEARCH_SX}
                />
              </Box>
            </Box>

            <TableContainer sx={{ overflowX: "auto" }}>
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...TH_SX, width: "30%" }}>Course Title</TableCell>
                    <TableCell sx={TH_SX}>Instructor</TableCell>
                    <TableCell sx={TH_SX}>Category</TableCell>
                    <TableCell sx={TH_SX}>Hours</TableCell>
                    <TableCell sx={TH_SX}>Submitted</TableCell>
                    <TableCell sx={TH_SX}>Status</TableCell>
                    <TableCell sx={{ ...TH_SX, textAlign: "right" }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredSubmissions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ py: "56px", textAlign: "center", border: 0 }}>
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px" }}>
                          <Box sx={{ width: 52, height: 52, borderRadius: "14px", background: DS.surface3, display: "flex", alignItems: "center", justifyContent: "center", color: DS.ink4 }}>
                            <ArticleIcon sx={{ fontSize: 22 }} />
                          </Box>
                          <Typography sx={{ fontSize: 14, fontWeight: 700, color: DS.ink }}>No submissions found</Typography>
                          <Typography sx={{ fontSize: 13, color: DS.ink3 }}>Try changing your filter</Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredSubmissions.map((sub, idx) => {
                      const { bg: catBg, color: catColor } = getCatConfig(sub.category || "", DS);
                      const isPend = (sub.status || "").toLowerCase() === "pending";
                      const isApproved = (sub.status || "").toLowerCase() === "approved";
                      const isLast = idx === filteredSubmissions.length - 1;
                      return (
                        <TableRow key={sub.id} hover>
                          <TableCell sx={{ ...TD_SX, ...(isLast && { borderBottom: 0 }) }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                              <Box
                                sx={{
                                  width: 38, height: 38, borderRadius: "10px",
                                  background: catBg, color: catColor,
                                  display: "flex", alignItems: "center", justifyContent: "center",
                                  flexShrink: 0,
                                }}
                              >
                                <SchoolIcon sx={{ fontSize: 17 }} />
                              </Box>
                              <Box>
                                <Typography sx={{ fontSize: 14, fontWeight: 700, color: DS.ink, letterSpacing: "-.01em" }}>
                                  {sub.title}
                                </Typography>
                                {sub.courseCode && (
                                  <Typography sx={{ fontSize: 12, color: DS.ink3, fontFamily: "monospace", fontWeight: 600 }}>
                                    {sub.courseCode}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ ...TD_SX, ...(isLast && { borderBottom: 0 }) }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                              <InstrAvatar name={sub.instructorName || ""} />
                              <Typography sx={{ fontSize: 13, fontWeight: 600, color: DS.ink2 }}>
                                {sub.instructorName || "—"}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ ...TD_SX, ...(isLast && { borderBottom: 0 }) }}>
                            <CategoryPill cat={sub.category} />
                          </TableCell>
                          <TableCell
                            sx={{
                              ...TD_SX, ...(isLast && { borderBottom: 0 }),
                              fontSize: 13, fontWeight: 700, color: DS.ink,
                            }}
                          >
                            {sub.totalHours ? `${sub.totalHours} hrs` : "—"}
                          </TableCell>
                          <TableCell
                            sx={{
                              ...TD_SX, ...(isLast && { borderBottom: 0 }),
                              fontSize: 13, color: DS.ink3,
                            }}
                          >
                            {sub.submittedAt ? new Date(sub.submittedAt).toLocaleDateString() : "—"}
                          </TableCell>
                          <TableCell sx={{ ...TD_SX, ...(isLast && { borderBottom: 0 }) }}>
                            <StatusPill status={(sub.status || "").toLowerCase()} />
                          </TableCell>
                          <TableCell
                            sx={{
                              ...TD_SX, ...(isLast && { borderBottom: 0 }),
                              textAlign: "right",
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center", gap: "6px", justifyContent: "flex-end" }}>
                              {isPend ? (
                                <Box
                                  component="button"
                                  onClick={() => handleViewDetails(sub, "course")}
                                  sx={{
                                    height: 32, px: "13px", borderRadius: "8px", border: 0,
                                    background: DS.green, color: "#fff", font: "inherit",
                                    fontSize: "12.5px", fontWeight: 700, cursor: "pointer",
                                    display: "inline-flex", alignItems: "center",
                                    boxShadow: `0 2px 8px -3px ${alpha(DS.green, 0.45)}`,
                                    "&:hover": { background: "var(--color-success)" },
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  Review
                                </Box>
                              ) : (
                                <Box
                                  component="button"
                                  onClick={() => handleViewDetails(sub, "course")}
                                  sx={{
                                    height: 32, px: "13px", borderRadius: "8px",
                                    border: `1.5px solid ${DS.line}`,
                                    background: DS.surface2, color: DS.ink2,
                                    font: "inherit", fontSize: "12.5px", fontWeight: 700,
                                    cursor: "pointer", display: "inline-flex", alignItems: "center",
                                    "&:hover": { background: DS.surface3, color: DS.ink },
                                  }}
                                >
                                  View
                                </Box>
                              )}
                              {isApproved && (
                                <Box
                                  component="button"
                                  onClick={() => handleManageInstructors(sub)}
                                  sx={{
                                    height: 32, px: "10px", borderRadius: "8px",
                                    border: `1.5px solid ${DS.line}`,
                                    background: DS.surface2, color: DS.ink2,
                                    font: "inherit", fontSize: "12.5px", fontWeight: 700,
                                    cursor: "pointer", display: "inline-flex", alignItems: "center",
                                    "&:hover": { background: DS.surface3 },
                                  }}
                                >
                                  <GroupIcon sx={{ fontSize: 15 }} />
                                </Box>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {/* Session Requests Panel */}
        {mainTab === 1 && (
          <Paper
            sx={{
              borderRadius: "18px", border: `1px solid ${DS.line}`,
              boxShadow: DS.shadowSm, overflow: "hidden",
            }}
          >
            {sessionRequestsLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Box
                  sx={{
                    display: "flex", alignItems: "center", gap: 1,
                    p: "12px 18px", borderBottom: `1px solid ${DS.line2}`,
                    background: "#fff", flexWrap: "wrap",
                  }}
                >
                  <SubTabBtn active={sessionSubFilter === "all"} onClick={() => setSessionSubFilter("all")} label="all" count={sessionRequests.length} />
                  <Box sx={{ width: "1px", height: 18, background: DS.line2, flexShrink: 0 }} />
                  <SubTabBtn active={sessionSubFilter === "pending"} onClick={() => setSessionSubFilter("pending")} label="pending" count={pendingSessionCount} />
                  <SubTabBtn
                    active={sessionSubFilter === "approved"}
                    onClick={() => setSessionSubFilter("approved")}
                    label="approved"
                    count={sessionRequests.filter((r) => (r.status || "").toLowerCase() === "approved").length}
                  />
                  <SubTabBtn
                    active={sessionSubFilter === "rejected"}
                    onClick={() => setSessionSubFilter("rejected")}
                    label="rejected"
                    count={sessionRequests.filter((r) => (r.status || "").toLowerCase() === "rejected").length}
                  />
                  <Box sx={{ ml: "auto" }}>
                    <TextField
                      size="small"
                      placeholder="Search…"
                      value={sessionSearch}
                      onChange={(e) => setSessionSearch(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ fontSize: 14, color: DS.ink4 }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={SEARCH_SX}
                    />
                  </Box>
                </Box>

                <TableContainer sx={{ overflowX: "auto" }}>
                  <Table sx={{ minWidth: 650 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ ...TH_SX, width: "30%" }}>Session / Course</TableCell>
                        <TableCell sx={TH_SX}>Instructor</TableCell>
                        <TableCell sx={TH_SX}>Date &amp; Time</TableCell>
                        <TableCell sx={TH_SX}>Location</TableCell>
                        <TableCell sx={TH_SX}>Submitted</TableCell>
                        <TableCell sx={TH_SX}>Status</TableCell>
                        <TableCell sx={{ ...TH_SX, textAlign: "right" }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredSessions.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} sx={{ py: "56px", textAlign: "center", border: 0 }}>
                            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px" }}>
                              <Box sx={{ width: 52, height: 52, borderRadius: "14px", background: DS.surface3, display: "flex", alignItems: "center", justifyContent: "center", color: DS.ink4 }}>
                                <CalendarTodayIcon sx={{ fontSize: 22 }} />
                              </Box>
                              <Typography sx={{ fontSize: 14, fontWeight: 700, color: DS.ink }}>No session requests found</Typography>
                              <Typography sx={{ fontSize: 13, color: DS.ink3 }}>Try changing your filter</Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredSessions.map((req, idx) => {
                          const isPend = (req.status || "pending").toLowerCase() === "pending";
                          const isLast = idx === filteredSessions.length - 1;
                          return (
                            <TableRow key={req.id} hover>
                              <TableCell sx={{ ...TD_SX, ...(isLast && { borderBottom: 0 }) }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                  <Box
                                    sx={{
                                      width: 38, height: 38, borderRadius: "10px",
                                      background: DS.surface3, color: DS.ink3,
                                      display: "flex", alignItems: "center", justifyContent: "center",
                                      flexShrink: 0,
                                    }}
                                  >
                                    <CalendarTodayIcon sx={{ fontSize: 17 }} />
                                  </Box>
                                  <Box>
                                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: DS.ink }}>
                                      {req.className || `Class #${req.classId}`}
                                    </Typography>
                                    <Typography sx={{ fontSize: 12, color: DS.ink3 }}>
                                      {req.sessionDate || "—"}
                                    </Typography>
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell sx={{ ...TD_SX, ...(isLast && { borderBottom: 0 }) }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                  <InstrAvatar name={req.instructorName || ""} />
                                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: DS.ink2 }}>
                                    {req.instructorName || "—"}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell sx={{ ...TD_SX, ...(isLast && { borderBottom: 0 }) }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 600, color: DS.ink2 }}>
                                  {req.sessionDate || "—"}
                                </Typography>
                                {req.startTime && req.endTime && (
                                  <Typography sx={{ fontSize: 12, color: DS.ink3 }}>
                                    {req.startTime} – {req.endTime}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell sx={{ ...TD_SX, ...(isLast && { borderBottom: 0 }), fontSize: 13, color: DS.ink2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  <Chip
                                    label={req.sessionType || "PHYSICAL"}
                                    size="small"
                                    variant="outlined"
                                    color={req.sessionType === "VIRTUAL" ? "info" : "default"}
                                  />
                                  {req.location || req.meetingLink || "—"}
                                </Box>
                              </TableCell>
                              <TableCell sx={{ ...TD_SX, ...(isLast && { borderBottom: 0 }), fontSize: 13, color: DS.ink3 }}>
                                {req.submittedAt ? new Date(req.submittedAt).toLocaleDateString() : "—"}
                              </TableCell>
                              <TableCell sx={{ ...TD_SX, ...(isLast && { borderBottom: 0 }) }}>
                                <StatusPill status={(req.status || "pending").toLowerCase()} />
                              </TableCell>
                              <TableCell sx={{ ...TD_SX, ...(isLast && { borderBottom: 0 }), textAlign: "right" }}>
                                {isPend ? (
                                  <Box
                                    component="button"
                                    onClick={() => handleViewDetails(req, "session")}
                                    sx={{
                                      height: 32, px: "13px", borderRadius: "8px", border: 0,
                                      background: DS.green, color: "#fff", font: "inherit",
                                      fontSize: "12.5px", fontWeight: 700, cursor: "pointer",
                                      display: "inline-flex", alignItems: "center",
                                      boxShadow: `0 2px 8px -3px ${alpha(DS.green, 0.45)}`,
                                      "&:hover": { background: "var(--color-success)" },
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    Review
                                  </Box>
                                ) : (
                                  <Box
                                    component="button"
                                    onClick={() => handleViewDetails(req, "session")}
                                    sx={{
                                      height: 32, px: "13px", borderRadius: "8px",
                                      border: `1.5px solid ${DS.line}`,
                                      background: DS.surface2, color: DS.ink2,
                                      font: "inherit", fontSize: "12.5px", fontWeight: 700,
                                      cursor: "pointer", display: "inline-flex", alignItems: "center",
                                      "&:hover": { background: DS.surface3, color: DS.ink },
                                    }}
                                  >
                                    View
                                  </Box>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </Paper>
        )}

        {/* Pending Registrations Panel */}
        {mainTab === 2 && (
          <Paper
            sx={{
              borderRadius: "18px", border: `1px solid ${DS.line}`,
              boxShadow: DS.shadowSm, overflow: "hidden",
            }}
          >
            {pendingRegistrationsLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer sx={{ overflowX: "auto" }}>
                <Table sx={{ minWidth: 650 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...TH_SX, width: "30%" }}>Student</TableCell>
                      <TableCell sx={TH_SX}>Class</TableCell>
                      <TableCell sx={{ ...TH_SX, textAlign: "right" }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pendingRegistrations.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} sx={{ py: "56px", textAlign: "center", border: 0 }}>
                          <Typography sx={{ fontSize: 14, fontWeight: 700, color: DS.ink }}>
                            No registrations awaiting approval
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      pendingRegistrations.map((reg, idx) => {
                        const isLast = idx === pendingRegistrations.length - 1;
                        const busy = registrationActionId === reg.id;
                        return (
                          <TableRow key={reg.id} hover>
                            <TableCell sx={{ ...TD_SX, ...(isLast && { borderBottom: 0 }) }}>
                              <Typography sx={{ fontSize: 14, fontWeight: 700, color: DS.ink }}>
                                {reg.studentName || `User #${reg.userId}`}
                              </Typography>
                              {reg.studentEmail && (
                                <Typography sx={{ fontSize: 12, color: DS.ink3 }}>
                                  {reg.studentEmail}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell sx={{ ...TD_SX, ...(isLast && { borderBottom: 0 }) }}>
                              <Typography sx={{ fontSize: 13, fontWeight: 600, color: DS.ink2 }}>
                                {reg.classTitle || `Class #${reg.classId}`}
                              </Typography>
                              {reg.courseNumber && (
                                <Typography sx={{ fontSize: 12, color: DS.ink3, fontFamily: "monospace" }}>
                                  {reg.courseNumber}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell sx={{ ...TD_SX, ...(isLast && { borderBottom: 0 }), textAlign: "right" }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: "6px", justifyContent: "flex-end" }}>
                                <Box
                                  component="button"
                                  disabled={busy}
                                  onClick={() => handleApproveRegistration(reg)}
                                  sx={{
                                    height: 32, px: "13px", borderRadius: "8px", border: 0,
                                    background: DS.green, color: "#fff", font: "inherit",
                                    fontSize: "12.5px", fontWeight: 700, cursor: busy ? "default" : "pointer",
                                    opacity: busy ? 0.6 : 1,
                                    "&:hover": { background: busy ? DS.green : "var(--color-success)" },
                                  }}
                                >
                                  Approve
                                </Box>
                                <Box
                                  component="button"
                                  disabled={busy}
                                  onClick={() => handleRejectRegistration(reg)}
                                  sx={{
                                    height: 32, px: "13px", borderRadius: "8px",
                                    border: `1.5px solid ${DS.line}`,
                                    background: DS.surface2, color: DS.ink2,
                                    font: "inherit", fontSize: "12.5px", fontWeight: 700,
                                    cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1,
                                    "&:hover": { background: busy ? DS.surface2 : DS.surface3 },
                                  }}
                                >
                                  Reject
                                </Box>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        )}

        {/* Review Dialog */}
        <Dialog
          open={detailOpen}
          onClose={handleCloseDetail}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: { borderRadius: "18px", overflow: "hidden" },
          }}
        >
          {selectedSubmission && (
            <>
              {/* Head */}
              <Box
                sx={{
                  display: "flex", alignItems: "flex-start", gap: "14px",
                  p: "20px 22px 18px", borderBottom: `1px solid ${DS.line2}`,
                }}
              >
                <Box
                  sx={{
                    width: 40, height: 40, borderRadius: "11px",
                    background: dCatBg, color: dCatColor,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {drawerType === "course" ? (
                    <SchoolIcon sx={{ fontSize: 18 }} />
                  ) : (
                    <CalendarTodayIcon sx={{ fontSize: 18 }} />
                  )}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{ fontSize: 17, fontWeight: 800, letterSpacing: "-.02em", color: DS.ink, fontFamily: "var(--font-display)" }}
                  >
                    {drawerType === "course"
                      ? selectedSubmission.title
                      : selectedSubmission.className || `Class #${selectedSubmission.classId}`}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: DS.ink3, mt: "3px" }}>
                    Submitted by {selectedSubmission.instructorName || "—"} ·{" "}
                    {selectedSubmission.submittedAt
                      ? new Date(selectedSubmission.submittedAt).toLocaleDateString()
                      : selectedSubmission.sessionDate || "—"}
                  </Typography>
                </Box>
                <Box
                  component="button"
                  onClick={handleCloseDetail}
                  sx={{
                    width: 32, height: 32, borderRadius: "9px",
                    border: `1px solid ${DS.line}`, background: "#fff", color: DS.ink3,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    cursor: "pointer", flexShrink: 0,
                    "&:hover": { background: DS.surface3, color: DS.ink },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 15 }} />
                </Box>
              </Box>

              {/* Body */}
              <DialogContent
                sx={{
                  p: "20px 22px",
                  display: "flex", flexDirection: "column", gap: "18px",
                }}
              >
                {drawerType === "course" ? (
                  <>
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px" }}>
                      <DetailField label="Category">
                        <CategoryPill cat={selectedSubmission.category} />
                      </DetailField>
                      {selectedSubmission.courseCode && (
                        <DetailField label="Course Code">
                          <Typography sx={{ fontSize: 13, fontWeight: 600, fontFamily: "monospace", color: DS.ink }}>
                            {selectedSubmission.courseCode}
                          </Typography>
                        </DetailField>
                      )}
                      <DetailField label="Total Hours">
                        <Typography sx={{ fontSize: 14, fontWeight: 600, color: DS.ink }}>
                          {selectedSubmission.totalHours ? `${selectedSubmission.totalHours} hrs` : "—"}
                        </Typography>
                      </DetailField>
                      {selectedSubmission.maxStudents && (
                        <DetailField label="Max Students">
                          <Typography sx={{ fontSize: 14, fontWeight: 600, color: DS.ink }}>
                            {selectedSubmission.maxStudents}
                          </Typography>
                        </DetailField>
                      )}
                      <DetailField label="Submitted">
                        <Typography sx={{ fontSize: 14, fontWeight: 600, color: DS.ink }}>
                          {selectedSubmission.submittedAt
                            ? new Date(selectedSubmission.submittedAt).toLocaleDateString()
                            : "—"}
                        </Typography>
                      </DetailField>
                      <DetailField label="Status">
                        <StatusPill status={(selectedSubmission.status || "").toLowerCase()} />
                      </DetailField>
                    </Box>

                    {selectedSubmission.description && (
                      <DetailField label="Description">
                        <Box
                          sx={{
                            background: DS.surface2, border: `1px solid ${DS.line2}`,
                            borderRadius: "12px", p: "14px 16px",
                            fontSize: "13.5px", color: DS.ink2, lineHeight: 1.6,
                          }}
                        >
                          {selectedSubmission.description}
                        </Box>
                      </DetailField>
                    )}

                    {selectedSubmission.objectives && (
                      <DetailField label="Learning Objectives">
                        <Box
                          sx={{
                            background: DS.surface2, border: `1px solid ${DS.line2}`,
                            borderRadius: "12px", p: "14px 16px",
                            fontSize: "13.5px", color: DS.ink2, lineHeight: 1.6,
                          }}
                        >
                          {selectedSubmission.objectives}
                        </Box>
                      </DetailField>
                    )}

                    {selectedSubmission.prerequisites && (
                      <DetailField label="Prerequisites">
                        <Typography sx={{ fontSize: "13.5px", color: DS.ink2 }}>
                          {selectedSubmission.prerequisites}
                        </Typography>
                      </DetailField>
                    )}

                    {selectedSubmission.equipmentNeeded && (
                      <DetailField label="Equipment Needed">
                        <Typography sx={{ fontSize: "13.5px", color: DS.ink2 }}>
                          {selectedSubmission.equipmentNeeded}
                        </Typography>
                      </DetailField>
                    )}

                    {selectedSubmission.justification && (
                      <DetailField label="Instructor Justification">
                        <Box
                          sx={{
                            background: DS.surface2, border: `1px solid ${DS.line2}`,
                            borderRadius: "12px", p: "14px 16px",
                            fontSize: "13.5px", color: DS.ink2, lineHeight: 1.6,
                            fontStyle: "italic",
                          }}
                        >
                          "{selectedSubmission.justification}"
                        </Box>
                      </DetailField>
                    )}

                    {selectedSubmission.reviewNotes && (
                      <Box
                        sx={{
                          background: DS.danger50, border: `1px solid ${DS.danger100}`,
                          borderRadius: "12px", p: "14px 16px",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 11, fontWeight: 700, letterSpacing: ".07em",
                            textTransform: "uppercase", color: DS.danger, mb: "6px",
                          }}
                        >
                          Rejection Reason
                        </Typography>
                        <Typography sx={{ fontSize: "13.5px", color: DS.ink2 }}>
                          {selectedSubmission.reviewNotes}
                        </Typography>
                      </Box>
                    )}
                  </>
                ) : (
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px" }}>
                    <DetailField label="Course">
                      <Typography sx={{ fontSize: 14, fontWeight: 600, color: DS.ink }}>
                        {selectedSubmission.className || `Class #${selectedSubmission.classId}`}
                      </Typography>
                    </DetailField>
                    <DetailField label="Instructor">
                      <Typography sx={{ fontSize: 14, fontWeight: 600, color: DS.ink }}>
                        {selectedSubmission.instructorName || "—"}
                      </Typography>
                    </DetailField>
                    <DetailField label="Date">
                      <Typography sx={{ fontSize: 14, fontWeight: 600, color: DS.ink }}>
                        {selectedSubmission.sessionDate || "—"}
                      </Typography>
                    </DetailField>
                    <DetailField label="Location">
                      <Typography sx={{ fontSize: 14, fontWeight: 600, color: DS.ink }}>
                        {selectedSubmission.location || selectedSubmission.meetingLink || "—"}
                      </Typography>
                    </DetailField>
                    <DetailField label="Format">
                      <Chip
                        label={selectedSubmission.sessionType || "PHYSICAL"}
                        size="small"
                        variant="outlined"
                        color={selectedSubmission.sessionType === "VIRTUAL" ? "info" : "default"}
                      />
                    </DetailField>
                    <DetailField label="Status">
                      <StatusPill status={(selectedSubmission.status || "pending").toLowerCase()} />
                    </DetailField>
                  </Box>
                )}

                {/* Reject reason field */}
                {drawerRejectMode && (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: "7px" }}>
                    <Typography
                      sx={{
                        fontSize: "11.5px", fontWeight: 700, letterSpacing: ".07em",
                        textTransform: "uppercase", color: DS.danger,
                      }}
                    >
                      Rejection Reason *
                    </Typography>
                    <TextField
                      multiline
                      rows={3}
                      fullWidth
                      value={drawerRejectText}
                      onChange={(e) => setDrawerRejectText(e.target.value)}
                      placeholder="Explain why this submission is not being approved…"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          fontSize: "13.5px", borderRadius: "10px",
                          background: DS.danger50,
                          "& fieldset": { borderColor: DS.danger100 },
                          "&:hover fieldset": { borderColor: DS.danger },
                          "&.Mui-focused fieldset": { borderColor: DS.danger },
                        },
                      }}
                    />
                  </Box>
                )}
              </DialogContent>

              {/* Footer */}
              <DialogActions
                sx={{
                  gap: "9px", p: "14px 22px 18px",
                  borderTop: `1px solid ${DS.line2}`,
                  background: DS.surface2,
                }}
              >
                {dIsPending ? (
                  drawerRejectMode ? (
                    <>
                      <Box
                        component="button"
                        onClick={() => setDrawerRejectMode(false)}
                        sx={{
                          height: 42, px: "20px", borderRadius: "12px",
                          border: `1.5px solid ${DS.line}`, background: "transparent",
                          color: DS.ink3, font: "inherit", fontSize: 14, fontWeight: 700,
                          cursor: "pointer",
                          "&:hover": { background: DS.surface3, color: DS.ink },
                        }}
                      >
                        Cancel
                      </Box>
                      <Box
                        component="button"
                        onClick={handleRejectConfirm}
                        disabled={!drawerRejectText.trim() || actionLoading}
                        sx={{
                          flex: 1, height: 42, px: "20px", borderRadius: "12px", border: 0,
                          background: DS.danger, color: "#fff", font: "inherit",
                          fontSize: 14, fontWeight: 700, cursor: "pointer",
                          display: "flex", justifyContent: "center", alignItems: "center",
                          "&:hover": { background: "#9B1B0E" },
                          "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
                        }}
                      >
                        {actionLoading ? "Submitting…" : "Confirm Rejection"}
                      </Box>
                    </>
                  ) : (
                    <>
                      <Box
                        component="button"
                        onClick={handleCloseDetail}
                        sx={{
                          height: 42, px: "20px", borderRadius: "12px",
                          border: `1.5px solid ${DS.line}`, background: "transparent",
                          color: DS.ink3, font: "inherit", fontSize: 14, fontWeight: 700,
                          cursor: "pointer",
                          "&:hover": { background: DS.surface3, color: DS.ink },
                        }}
                      >
                        Cancel
                      </Box>
                      <Box
                        component="button"
                        onClick={() => setDrawerRejectMode(true)}
                        sx={{
                          height: 42, px: "20px", borderRadius: "12px",
                          border: `1.5px solid ${DS.danger100}`,
                          background: DS.danger50, color: DS.danger,
                          font: "inherit", fontSize: 14, fontWeight: 700,
                          cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "8px",
                          "&:hover": { background: DS.danger100 },
                        }}
                      >
                        <CancelIcon sx={{ fontSize: 14 }} />
                        Reject
                      </Box>
                      <Box
                        component="button"
                        onClick={handleApprove}
                        disabled={actionLoading}
                        sx={{
                          flex: 1, height: 42, px: "20px", borderRadius: "12px", border: 0,
                          background: DS.green, color: "#fff", font: "inherit",
                          fontSize: 14, fontWeight: 700, cursor: "pointer",
                          display: "flex", justifyContent: "center", alignItems: "center", gap: "8px",
                          boxShadow: `0 4px 12px -4px ${alpha(DS.green, 0.45)}`,
                          "&:hover": { background: DS.greenDk },
                          "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
                        }}
                      >
                        <CheckCircleIcon sx={{ fontSize: 14 }} />
                        {actionLoading ? "Approving…" : "Approve"}
                      </Box>
                    </>
                  )
                ) : (
                  <Box
                    component="button"
                    onClick={handleCloseDetail}
                    sx={{
                      flex: 1, height: 42, px: "20px", borderRadius: "12px",
                      border: `1.5px solid ${DS.line}`, background: "transparent",
                      color: DS.ink3, font: "inherit", fontSize: 14, fontWeight: 700,
                      cursor: "pointer", display: "flex", justifyContent: "center", alignItems: "center",
                      "&:hover": { background: DS.surface3, color: DS.ink },
                    }}
                  >
                    Close
                  </Box>
                )}
              </DialogActions>
            </>
          )}
        </Dialog>

        {/* Manage Instructors Dialog */}
        <Dialog
          open={instructorDialogOpen}
          onClose={() => setInstructorDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <GroupIcon color="primary" />
              Manage Qualified Instructors
            </Box>
          </DialogTitle>
          <DialogContent>
            {instructorSubmission && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Select instructors qualified to teach{" "}
                  <strong>{instructorSubmission.title}</strong>. These instructors will be
                  available for assignment during semester planning.
                </Typography>
                <Autocomplete
                  multiple
                  options={allInstructors}
                  getOptionLabel={(opt) => opt.fullName || opt.username || String(opt.id)}
                  value={allInstructors.filter((i) => selectedInstructors.includes(i.id))}
                  onChange={(_, newVal) => setSelectedInstructors(newVal.map((v) => v.id))}
                  isOptionEqualToValue={(opt, val) => opt.id === val.id}
                  renderInput={(params) => (
                    <TextField {...params} label="Qualified Instructors" placeholder="Search instructors..." />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((opt, idx) => (
                      <Chip
                        {...getTagProps({ index: idx })}
                        key={opt.id}
                        label={opt.fullName || opt.username}
                        size="small"
                      />
                    ))
                  }
                />
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={() => setInstructorDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSaveInstructors}
              disabled={instructorSaving || selectedInstructors.length === 0}
            >
              {instructorSaving ? "Saving..." : "Save Instructors"}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
}
